import {useCallback, useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
  Contract,
  ShipmentPreferredCurrencyEnum,
  ChargeCategory,
  ContractAdditionalStopChargesTypeEnum,
  ContractRateCurrencyEnum,
  ApplicableContract
} from '@shipwell/backend-core-singlerequestparam-sdk';
import {CARRIER_RELATIONSHIPS_QUERY_KEY, FUEL_SURCHARGE_TABLE_QUERY_KEY} from 'App/data-hooks/queryKeys';
import {getFuelSurchargeTable} from 'App/api/rateTables';
import {FuelSurchargeTableType} from 'src/@types/fuelSurchargeTableType';
import {findMatchingFuelSurchargeRate} from 'App/containers/userCompany/fuelSurcharges/utils/findMatchingFuelSurchargeRate';
import {chargeLineItemDefaults} from 'App/utils/globals';
import {getRateTotal} from 'App/utils/tenderUtils';
import {roundNumberToSpecificDecimals} from 'App/utils/mathHelpers';
import {getCarrierRelationshipsByIdPromise} from 'App/api/carriers/carrierRelationship';
import {RATE_TYPE_RATE_TABLE} from 'App/containers/userCompany/rateTables/constants';

type UseSelectedContractParams = {
  percentOfLineHaul?: number;
  additionalStops?: number;
  distance: number;
  isBulkShipments: boolean;
  preferredCurrency?: ContractRateCurrencyEnum | ShipmentPreferredCurrencyEnum;
};

export const isApplicableContract = (option: Contract | ApplicableContract): option is ApplicableContract => {
  return 'contract' in option;
};

export const useSelectedContract = ({
  distance,
  percentOfLineHaul,
  additionalStops,
  isBulkShipments,
  preferredCurrency
}: UseSelectedContractParams) => {
  const [selectedApplicableContract, setSelectedApplicableContract] = useState<ApplicableContract>();
  const [fuelSurchargeRate, setFuelSurchargeRate] = useState<number>();
  const selectedContract = selectedApplicableContract?.contract;

  const fuelSurchargeTableQuery = useQuery<{body: FuelSurchargeTableType}>(
    [FUEL_SURCHARGE_TABLE_QUERY_KEY, selectedContract?.fuel_surcharge_table],
    () => getFuelSurchargeTable(selectedContract?.fuel_surcharge_table),
    {
      enabled: !!selectedContract?.fuel_surcharge_table && !!distance,
      onSuccess: (data) => {
        const matchingFuelSurchargeRate = findMatchingFuelSurchargeRate({
          fuelSurchargeTable: data.body,
          rateToMatch: undefined
        });
        if (matchingFuelSurchargeRate && !!distance) {
          setFuelSurchargeRate(
            getRateTotal({
              distance,
              rateType: data.body.rate_type,
              baseRate: matchingFuelSurchargeRate,
              percentOfLineHaul
            })
          );
        }
      }
    }
  );

  useEffect(() => {
    if (!selectedContract?.fuel_surcharge_table) {
      setFuelSurchargeRate(undefined);
    }
  }, [selectedContract?.fuel_surcharge_table]);

  const {data: {data: contractCarrierRelationship} = {}, ...carrierRelationshipsQuery} = useQuery(
    [CARRIER_RELATIONSHIPS_QUERY_KEY, selectedContract?.carrier_relationship],
    () => getCarrierRelationshipsByIdPromise(selectedContract?.carrier_relationship || ''),
    {enabled: !!selectedContract}
  );

  const getAdditonalStopLineItems = ({preferredCurrency}: {preferredCurrency?: ShipmentPreferredCurrencyEnum}) => {
    // if no additional stops, return an empty array
    if (!additionalStops) {
      return [];
    }
    const additionalStopCharge = selectedContract?.additional_stop_charge_flat_fee;
    const isChargePerStop =
      selectedContract?.additional_stop_charges_type === ContractAdditionalStopChargesTypeEnum.ChargePerAdditionalStop;

    // if contract uses a flat fee for all contracts
    if (isChargePerStop && additionalStopCharge) {
      return [
        {
          category: ChargeCategory.Other,
          unit_amount: roundNumberToSpecificDecimals(additionalStopCharge, 4),
          unit_amount_currency: preferredCurrency,
          unit_name: 'Additional Stop Charge',
          unit_quantity: additionalStops
        }
      ];
    }

    // if contract specifies custom pricing on each additional stop
    const customStopCharges =
      selectedContract?.additional_stops?.map((stop: object) => {
        if (
          'additional_stop_charge' in stop &&
          stop?.additional_stop_charge &&
          typeof stop.additional_stop_charge === 'number'
        ) {
          return stop.additional_stop_charge;
        }
        return 0;
      }) || [];
    return customStopCharges.map((charge: number) => ({
      category: ChargeCategory.Other,
      unit_amount: roundNumberToSpecificDecimals(charge, 4),
      unit_amount_currency: preferredCurrency,
      unit_name: 'Additional Stop Charge',
      unit_quantity: 1
    }));
  };

  // used only when no shipment is available
  // otherwise use the backend endpoint to calculate contract values
  const getChargeLineItems = (preferredCurrency?: ShipmentPreferredCurrencyEnum) => {
    // does NOT support rate tables
    return preferredCurrency && selectedContract?.rate_type !== RATE_TYPE_RATE_TABLE
      ? [
          {
            category: ChargeCategory.Lh,
            // rounding number to a max of 4 decimals to prevent BE error
            unit_amount: roundNumberToSpecificDecimals(
              // if only one shipment in shipments, treat as individual, not bulk
              !isBulkShipments
                ? getRateTotal({
                    distance,
                    rateType: selectedContract?.rate_type || '',
                    baseRate: selectedContract?.rate || 0,
                    percentOfLineHaul,
                    minimumRate: selectedContract?.minimum_rate
                  })
                : //if bulk contracts then we need to send the contract rate to Opus
                  selectedContract?.rate || 0,
              4
            ),
            unit_amount_currency: preferredCurrency,
            unit_name: chargeLineItemDefaults.unit_name,
            unit_quantity: Number(chargeLineItemDefaults.unit_quantity)
          },
          ...(fuelSurchargeRate
            ? [
                {
                  category: ChargeCategory.Fsc,
                  unit_amount: roundNumberToSpecificDecimals(fuelSurchargeRate, 4),
                  unit_amount_currency: preferredCurrency,
                  unit_name: 'Fuel Surcharge',
                  unit_quantity: 1
                }
              ]
            : []),
          ...getAdditonalStopLineItems({preferredCurrency})
        ]
      : undefined;
  };

  return {
    selectedContract,
    handleContractChange: useCallback((value?: Contract | ApplicableContract) => {
      if (value) {
        if (isApplicableContract(value)) {
          setSelectedApplicableContract(value);
        } else {
          setSelectedApplicableContract({contract: value});
        }
      }
    }, []),
    fuelSurchargeRate,
    isLoading: fuelSurchargeTableQuery.isFetching || carrierRelationshipsQuery.isFetching,
    contractCarrierRelationship: selectedContract ? contractCarrierRelationship : undefined,
    chargeLineItems: getChargeLineItems(preferredCurrency || selectedContract?.rate_currency)
  };
};
